<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import {
  checkFileSizeLimit,
  formatBytes,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const files = defineModel({ type: Array, default: () => [] });

const { t } = useI18n();
const globalConfig = useMapGetter('globalConfig/get');

const fileInput = ref(null);
const isDragging = ref(false);

const addFiles = incoming => {
  const limit = resolveMaximumFileUploadSize(
    globalConfig.value.maximumFileUploadSize
  );
  const accepted = Array.from(incoming).filter(file =>
    checkFileSizeLimit(file, limit)
  );
  if (accepted.length < incoming.length) {
    useAlert(t('TICKETS.ATTACHMENTS.SIZE_LIMIT', { size: limit }));
  }
  files.value = [...files.value, ...accepted];
};

const onFileSelected = event => {
  addFiles(event.target.files);
  event.target.value = '';
};

const onDrop = event => {
  isDragging.value = false;
  addFiles(event.dataTransfer.files);
};

// Chamado pelo modal para o print colado em qualquer campo do formulario virar anexo.
const onPaste = event => {
  const pasted = event.clipboardData?.files;
  if (!pasted?.length) return;
  event.preventDefault();
  addFiles(pasted);
};

const removeFile = index => {
  files.value = files.value.filter((_, fileIndex) => fileIndex !== index);
};

defineExpose({ onPaste });
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      class="flex flex-wrap items-center justify-center gap-1 px-4 py-3 text-sm border border-dashed rounded-lg text-n-slate-11"
      :class="isDragging ? 'border-n-blue-9 bg-n-alpha-1' : 'border-n-weak'"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <span>{{ t('TICKETS.ATTACHMENTS.DROP_HINT') }}</span>
      <Button
        variant="link"
        size="sm"
        type="button"
        :label="t('TICKETS.ATTACHMENTS.BROWSE')"
        @click="fileInput.click()"
      />
      <input
        ref="fileInput"
        type="file"
        multiple
        class="hidden"
        @change="onFileSelected"
      />
    </div>
    <ul v-if="files.length" class="flex flex-col gap-1 m-0 list-none">
      <li
        v-for="(file, index) in files"
        :key="`${file.name}-${index}`"
        class="flex items-center gap-2 px-2 py-1 text-sm rounded-md bg-n-alpha-1 text-n-slate-12"
      >
        <Icon icon="i-lucide-paperclip" class="size-4 text-n-slate-11" />
        <span class="flex-1 truncate">{{ file.name }}</span>
        <span class="text-xs text-n-slate-11">{{
          formatBytes(file.size)
        }}</span>
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          type="button"
          icon="i-lucide-x"
          :aria-label="t('TICKETS.ATTACHMENTS.REMOVE', { filename: file.name })"
          @click="removeFile(index)"
        />
      </li>
    </ul>
  </div>
</template>
